// Centralized logger with configurable levels

class Logger {
    private isDebugMode: boolean;

    constructor() {
        this.isDebugMode = this.determineDebugMode();
        // Expose logger to the window for manual debugging
        if (typeof window !== 'undefined') {
            (window as Window & { logger: Logger }).logger = this;
        }
    }

    private determineDebugMode(): boolean {
        if (typeof window === 'undefined') {
            return false; // No logging in SSR or non-browser environments
        }
        
        const urlParams = new URLSearchParams(window.location.search);
        // Highest priority: URL parameter to force enable
        if (urlParams.get('debug') === 'true') {
            // Use console.log directly here so this message always shows up
            console.log("Logger: Debug mode force-enabled via URL parameter.");
            return true;
        }

        // Next priority: localStorage for runtime toggle
        try {
            const localStorageValue = localStorage.getItem('debug');
            if (localStorageValue !== null) {
                return localStorageValue === 'true';
            }
        } catch {
            // Ignore blocked storage; environment-based defaults still apply.
        }

        // Environment-based default
        // - Always off if explicitly disabled via env
        // - On in local dev and Vercel preview deployments (*.vercel.app)
        // - Off on production custom domains
        if (import.meta.env.VITE_DISABLE_DEBUG === 'true') {
            return false;
        }

        const isDev = import.meta.env.MODE === 'development';
        let isVercelPreview = false;
        try {
            const host = window.location.hostname || '';
            isVercelPreview = /\.vercel\.app$/.test(host);
        } catch {
            // ignore
        }
        return isDev || isVercelPreview;
    }

    public log(...args: unknown[]): void {
        if (this.isDebugMode) {
            console.log(...args);
        }
    }

    public error(...args: unknown[]): void {
        // Errors are always logged
        console.error(...args);
    }

    public warn(...args: unknown[]): void {
        if (this.isDebugMode) {
            console.warn(...args);
        }
    }
    
    public info(...args: unknown[]): void {
        if (this.isDebugMode) {
            console.info(...args);
        }
    }

    public enableDebug(): void {
        this.isDebugMode = true;
        try {
            localStorage.setItem('debug', 'true');
        } catch {
            // In-memory debug mode still applies for the current page.
        }
        console.log('Logger: Debug mode enabled. Refresh the page for full effect.');
    }

    public disableDebug(): void {
        this.isDebugMode = false;
        try {
            localStorage.setItem('debug', 'false');
        } catch {
            // In-memory debug mode still applies for the current page.
        }
        // We still need a console.log here to confirm the action was taken.
        console.log('Logger: Debug mode disabled. Refresh the page for full effect.');
    }
}

export const logger = new Logger();

// Add a declaration to the Window interface to avoid TypeScript errors
declare global {
    interface Window {
        logger: Logger;
    }
}
